import { createContext, useContext, useMemo, useState } from 'react';

const I18nContext = createContext({
  lang: 'en',
  t: (k) => k,
  toggleLang: () => {},
});

const messages = {
  en: {
    projects: 'Projects',
    about: 'About Me',
    contact: 'Contact',
    hireMe: 'Hire Me',
    heroTitle: 'Hi, I am Ibrahim Akgül',
    heroSubtitle: 'Freelance & XR Software Engineer',
    downloadCv: 'Download CV',
    followMe: 'Follow me',
    contactDetails: 'Contact details',
    moreProjects: 'More Projects',
    projectsPortfolio: 'Projects portfolio',
    searchPrompt: 'Search projects by title or filter by category',
    searchPlaceholder: 'Search Projects',
    allProjects: 'All Projects',
  category_web: 'Web Application',
  category_mobile: 'Mobile Application',
  category_uiux: 'UI/UX Design',
  category_branding: 'Branding',
  category_ar: 'AR Application',
  category_xr_app: 'XR App',
  category_vr_app: 'VR App',
  category_ar_app: 'AR App',
  category_webxr: 'WebXR',
  category_gaussian_splatting: 'Gaussian Splatting',
  category_fpv_footage: 'FPV',
  category_research: 'Research',
  counter_years: 'Years of experience',
  counter_projects: 'Total number of projects',
  counter_clients: 'Clients Served',
  counter_awards: 'Awards & Recognitions',
  lang_flag_label: 'Switch to French',
    clientsHeading: 'Some of the brands that trust me',
    contactFormTitle: 'Contact Form',
    fullName: 'Full Name',
    yourName: 'Your Name',
    email: 'Email',
    yourEmail: 'Your email',
    subject: 'Subject',
    message: 'Message',
    sendMessage: 'Send Message',
  formErrorTitle: 'Please complete the form',
  formErrorMessage: 'All fields are required. Please fill out every field.',
  formSuccessTitle: 'Message sent',
  formSuccessMessage: "Thanks! I'll get back to you soon.",
  formSendErrorTitle: 'Failed to send',
  formSendErrorMessage: 'Something went wrong. Please try again later.',
  projectObjectives: 'Objectives',
  projectTechnologies: 'Tools & Technologies',
  projectDetailsHeading: 'Challenge',
  relatedProjects: 'Related Projects',
  ciiKicker: 'Official accreditation',
  ciiTitle: 'Accredited for the French Innovation Tax Credit (CII)',
  ciiText:
    'I hold the CII accreditation n°32271754, granted by the French Ministry of the Economy (DRIEETS Île-de-France) for the years 2026 to 2030. French SMEs that hire me to design prototypes or innovative new products can claim back 20% of my invoiced services as a tax credit (up to €400,000 of eligible expenses per year), with no extra paperwork on their side.',
  ciiPoint1: '20% tax credit on invoiced innovation work',
  ciiPoint2: 'Up to €80,000 of tax credit per year',
  ciiPoint3: 'Valid for the years 2026 to 2030',
  ciiDownload: 'View the official decision (PDF)',
  },
  fr: {
    projects: 'Projets',
    about: 'À propos de moi',
    contact: 'Contact',
  hireMe: 'Me recruter',
    heroTitle: 'Salut, je suis Ibrahim Akgül',
    heroSubtitle: 'Freelance & Ingénieur logiciel XR',
    downloadCv: 'Télécharger le CV',
    followMe: 'Suivez-moi',
    contactDetails: 'Coordonnées',
    moreProjects: 'Plus de projets',
    projectsPortfolio: 'Portfolio de projets',
    searchPrompt: 'Recherchez par titre ou filtrez par catégorie',
    searchPlaceholder: 'Rechercher des projets',
    allProjects: 'Tous les projets',
  category_web: 'Application Web',
  category_mobile: 'Application Mobile',
  category_uiux: 'Design UI/UX',
  category_branding: 'Branding',
  category_ar: 'Application AR',
  category_xr_app: 'Application XR',
  category_vr_app: 'Application VR',
  category_ar_app: 'Application AR',
  category_webxr: 'WebXR',
  category_gaussian_splatting: 'Gaussian Splatting',
  category_fpv_footage: 'Captation FPV',
  category_research: 'Recherche',
  counter_years: "Années d'expérience",
  counter_projects: 'Nombre total de projets',
  counter_clients: 'Clients accompagnés',
  counter_awards: 'Récompenses & distinctions',
  lang_flag_label: 'Passer en anglais',
    clientsHeading: 'Ils me font confiance',
    contactFormTitle: 'Formulaire de contact',
    fullName: 'Nom complet',
    yourName: 'Votre nom',
    email: 'Email',
    yourEmail: 'Votre email',
    subject: 'Sujet',
    message: 'Message',
    sendMessage: 'Envoyer le message',
  formErrorTitle: 'Veuillez compléter le formulaire',
  formErrorMessage: 'Tous les champs sont obligatoires. Merci de tout remplir.',
  formSuccessTitle: 'Message envoyé',
  formSuccessMessage: 'Merci ! Je vous répondrai très vite.',
  formSendErrorTitle: "Échec de l'envoi",
  formSendErrorMessage: 'Une erreur est survenue. Veuillez réessayer plus tard.',
  projectObjectives: 'Objectifs',
  projectTechnologies: 'Outils et technologies',
  projectDetailsHeading: 'Défi',
  relatedProjects: 'Projets liés',
  ciiKicker: 'Agrément officiel',
  ciiTitle: "Agréé Crédit d'Impôt Innovation (CII)",
  ciiText:
    "Je suis titulaire de l'agrément CII n°32271754, délivré par le ministère de l'Économie (DRIEETS Île-de-France) au titre des années 2026 à 2030. Les PME qui me confient la conception de prototypes ou de produits nouveaux innovants peuvent récupérer 20 % de mes prestations sous forme de crédit d'impôt (dans la limite de 400 000 € de dépenses éligibles par an), sans aucune démarche d'agrément de leur côté.",
  ciiPoint1: "20 % de crédit d'impôt sur les prestations facturées",
  ciiPoint2: "Jusqu'à 80 000 € de crédit d'impôt par an",
  ciiPoint3: 'Agrément valable de 2026 à 2030',
  ciiDownload: "Consulter la décision officielle (PDF)",
  },
};

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(
    typeof window !== 'undefined' ? localStorage.getItem('lang') || 'en' : 'en'
  );

  const toggleLang = () => {
    const next = lang === 'en' ? 'fr' : 'en';
    setLang(next);
    if (typeof window !== 'undefined') localStorage.setItem('lang', next);
  };

  const t = (key) => messages[lang][key] ?? key;

  const value = useMemo(() => ({ lang, t, toggleLang }), [lang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
